<ng-template #template>
  <nav class="dropdown-options-container">
    <button
      class="dropdown-option"
      (click)="addItem()"
      [attr.aria-label]="'Add new ' + localisedItem">
      {{ T.common.add_new_item | translate: { item: localisedItem } }}
    </button>
    <button
      class="dropdown-option"
      (click)="addItems()"
      [attr.aria-label]="addFromTemplateText">
      {{ addFromTemplateText }}
    </button>
  </nav>
</ng-template>

<!-- Add Item Button Template -->
<ng-template #addItemButton>
  <ng-container *ngIf="!isMyTrack; else myTrackButton">
    <button
      class="add-checklist-button"
      ngDropdown
      [templateRef]="template"
      [closeUponSelection]="true"
      [attr.aria-label]="'Add new ' + localisedItem">
      <i class="material-symbols-outlined check-icon" aria-hidden="true">add</i>
      <span class="ms-1">{{ T.common.add_new_item | translate: { item: localisedItem } }}</span>
    </button>
  </ng-container>
  <ng-template #myTrackButton>
    <ng-container *ngIf="mobile; else desktopMyTrackButton">
      <app-button-icon-square
        size="small"
        icon="add"
        class="mr-8"
        buttonTheme="white"
        [attr.aria-label]="'Add new ' + localisedItem"
        (clicked)="addItem()">
      </app-button-icon-square>
    </ng-container>
    <ng-template #desktopMyTrackButton>
      <button
        class="add-checklist-button"
        (click)="addItem()"
        [attr.aria-label]="'Add new ' + localisedItem">
        <i class="material-symbols-outlined check-icon" aria-hidden="true">add</i>
        <span class="ms-1">{{ T.common.add_new_item | translate: { item: localisedItem } }}</span>
      </button>
    </ng-template>
  </ng-template>
</ng-template>

<!-- Toggle Button Template -->
<ng-template #toggleButton>
  <app-toggle-button
    [objects]="toggleOptions"
    [currentlySelectedObject]="selectedToggleOption"
    [bindProperty]="'displayText'"
    role="group"
    [attr.aria-label]="'Filter ' + localisedItems"
    (clicked)="onShowHidePastActivities($event)"
  ></app-toggle-button>
</ng-template>

<article class="checklist-component-wrapper" role="region" [attr.aria-label]="localisedItems + ' section'">
  <!-- Header Section -->
  <header *ngIf="!isEmptyState" class="checklist-header">
    <!-- V1 Header -->
    <ng-container *ngIf="!useHeaderV2">
      <section class="header-top-right" [ngClass]="{'stretched-header': !showHeaderButtons && !showQuickAdd}">
        <h2 class="header-title"
          [attr.aria-label]="localisedItems + ', ' + groupedChecklistItems.length + ' ' + (groupedChecklistItems.length === 1 ? localisedItem : localisedItems)">
          {{ localisedItems }}
        </h2>
        <ng-container *ngIf="!hideAddButtonFromHeader">
          <ng-container *ngTemplateOutlet="addItemButton"></ng-container>
        </ng-container>
      </section>

      <!-- Quick Add or Toggle -->
      <section class="checklist-quick-add mt-8" *ngIf="showQuickAdd">
        <app-quick-add-input
          [placeholder]="quickAddPlaceholder"
          [attr.aria-label]="'Quick add ' + localisedItem"
          (titleChange)="onQuickAddChange($event)">
        </app-quick-add-input>
        <ng-container *ngTemplateOutlet="toggleButton"></ng-container>
      </section>
      <section *ngIf="!showQuickAdd && isMyTrack">
        <ng-container *ngTemplateOutlet="toggleButton"></ng-container>
      </section>
    </ng-container>

    <!-- V2 Header -->
    <section class="header-v2" *ngIf="useHeaderV2">
      <div class="left">
        <h2 class="header-title" [attr.aria-label]="localisedItems + ', ' + filteredItems.length + ' ' + (filteredItems.length === 1 ? localisedItem : localisedItems)">
          {{ localisedItems }}
        </h2>
      </div>
      <nav class="right" *ngIf="!hideAddButtonFromHeader" role="toolbar" [attr.aria-label]="localisedItems + ' actions'">
        <button
          class="add-checklist-button"
          ngDropdown
          [templateRef]="template"
          [closeUponSelection]="true"
          [attr.aria-label]="'Add new ' + localisedItem">
          <i class="material-symbols-outlined check-icon" aria-hidden="true">add</i>
          <span class="pt-1 ms-1">{{ T.common.add_new_item | translate: { item: localisedItem } }}</span>
        </button>
        <app-button-transparent
          icon="unfold_more"
          [text]="isExpandedAll ? (T.common.collapse_all | translate) : (T.common.expand_all | translate)"
          [attr.aria-label]="isExpandedAll ? ('Collapse all ' + localisedItems) : ('Expand all ' + localisedItems)"
          [attr.aria-expanded]="isExpandedAll"
          (clicked)="toggleExpand()"
        ></app-button-transparent>
      </nav>
    </section>

    <!-- Checklist Summary -->
    <section class="checklist-summary-wrapper">
      <app-checklist-summary
        *ngIf="showChecklistSummary"
        [checklistItems]="filteredItems"
        [isExpandedAll]="isExpandedAll"
        [showExpandBtn]="!isInJobOrIncidentDetails"
        role="status"
        [attr.aria-live]="'polite'"
        (toggleExpand)="toggleExpand()">
      </app-checklist-summary>
    </section>
  </header>

  <!-- Main Content -->
  @if(!loading) {
    <main class="checklist-wrapper custom-scrollbar" resized (resizable)="onResized()">

      <!-- Card Templates -->
      <ng-template #cardTemplate let-item="item">
        <app-checklist-item-card
          [loading]="loadingChecklistItems"
          [showOwnerLozenge]="showOwner"
          [checklistType]="checklistType"
          style="width: 100%"
          [item]="item"
          [parentItem]="parentItem"
          [userHasWriteAccess]="canEdit"
          role="article"
          [attr.aria-label]="getChecklistItemAriaLabel(item)"
          (viewItem)="onView($event)"
          (itemCompleted)="updateItem($event)"
        >
        </app-checklist-item-card>
      </ng-template>

      <ng-template #cardGroupTemplate let-item="item">
        <app-checklist-group-card
          [loading]="loadingChecklistItems"
          [canEdit]="canEdit"
          [groupTitle]="item.groupTitle"
          [expanded]="isExpanded(item.groupTitle)"
          [checklistItems]="item.checklistItems"
          [expandAsDrawer]="isInJobOrIncidentDetails"
          role="listitem"
          [attr.aria-label]="getGroupChecklistAriaLabel(item)"
          (expand)="expandItem($event, item)"
          [draggable]="showQuickAdd"
          (viewItem)="onView($event)"
          (itemCompleted)="updateItem($event)"
          (attachmentsClicked)="onAttachmentsClicked()"
        >
        </app-checklist-group-card>
      </ng-template>

      <!-- Non-grouped Checklist -->
      @if(!isEmptyState && filteredItems.length) {
        <app-paginated-cards *ngIf="!useGroupedChecklist"
          [paginationId]="paginationId"
          [trackByProperty]="'updated'"
          [items]="filteredItems"
          [cardTemplate]="cardTemplate"
          [draggable]="canEdit"
          role="list"
          [attr.aria-label]="localisedItems + ' list'"
          (onDrag)="onItemsViewOrderChanged($event)">
        </app-paginated-cards>

        <!-- Grouped Checklist -->
        <app-paginated-cards *ngIf="useGroupedChecklist"
          [paginationId]="paginationId"
          [trackByProperty]="'groupTitle'"
          [items]="groupedChecklistItems"
          [cardTemplate]="cardGroupTemplate"
          [draggable]="false"
          role="list"
          [attr.aria-label]="localisedItems + ' list'"
          (onDrag)="onItemsViewOrderChanged($event)">
        </app-paginated-cards>
      }

      <!-- Empty State -->
      <section class="empty-state" *ngIf="isEmptyState" role="status" [attr.aria-live]="'polite'">
        <header class="d-flex align-items-center mb-2" [ngClass]="{'justify-content-between': !isMyTrack}">
          <h2 class="header-title" [attr.aria-label]="(T.defaultLocalizations.checklist_item.many | translate) + ', 0 ' + localisedItems">
            {{ T.defaultLocalizations.checklist_item.many | translate }}
          </h2>

          <ng-container *ngIf="!hideAddButtonFromHeader && !isMyTrack">
            <button
              class="add-checklist-button"
              ngDropdown
              [templateRef]="template"
              [closeUponSelection]="true"
              [attr.aria-label]="'Add new ' + localisedItem">
              <i class="material-symbols-outlined check-icon" aria-hidden="true">add</i>
              <span class="ms-1">{{ T.common.add_new_item | translate: { item: localisedItem } }}</span>
            </button>
          </ng-container>

          <nav class="d-flex align-items-center justify-content-between w-100" *ngIf="isMyTrack">
            <button
              class="add-checklist-button"
              ngDropdown
              [templateRef]="template"
              [closeUponSelection]="true"
              [attr.aria-label]="'Add new ' + localisedItem">
              <i class="material-symbols-outlined check-icon" aria-hidden="true">add</i>
              <span class="ms-1">{{ T.common.add_new_item | translate: { item: localisedItem } }}</span>
            </button>
            <ng-container *ngTemplateOutlet="toggleButton"></ng-container>
          </nav>
        </header>
        <app-empty-state
          [isMobileChecklist]="isMobile"
          [hideIcon]="true"
          [isClickable]="false"
          [message]="noResultsTitle"
          [attr.aria-label]="'You dont have ' + localisedItems + ' yet. Add new ' + localisedItem + ' button available'">
        </app-empty-state>
      </section>
    </main>
  }


  <!-- Loading State -->
  <ng-container *ngIf="loading">
    <app-loading role="status" [attr.aria-live]="'polite'" [attr.aria-label]="'Loading ' + localisedItems">{{ T.common.loading | translate }}...</app-loading>
  </ng-container>
</article>
